import React, { useMemo, useState } from 'react';
import type { ChangeOrderRow } from 'src/types/changeOrder.type';
import { formatProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

function badge(status: string) {
    const base = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border';
    switch (status) {
        case 'APPROVED':
            return `${base} bg-emerald-50 border-emerald-200 text-emerald-700`;
        case 'REJECTED':
            return `${base} bg-rose-50 border-rose-200 text-rose-700`;
        case 'WAITING_COUNTERPART':
            return `${base} bg-amber-50 border-amber-200 text-amber-700`;
        default:
            return `${base} bg-gray-50 border-gray-200 text-gray-700`;
    }
}

export default function ChangeOrderList({
    rows,
    loading,
    onRowClick,
    onCreate,
    canEdit,
    actionLabel = '계약으로 이동',
}: {
    rows: ChangeOrderRow[];
    loading: boolean;
    onRowClick: (id: string) => void;
    onCreate?: () => void;
    canEdit?: boolean;
    actionLabel?: string;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';
    const [q, setQ] = useState('');

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return rows;
        return rows.filter((r) =>
            String(r.title ?? '')
                .toLowerCase()
                .includes(t),
        );
    }, [rows, q]);

    return (
        <div className="rounded-2xl border bg-white overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
                <div className="text-sm font-medium">List</div>
                <div className="flex items-center gap-2">
                    <input
                        className="w-56 max-w-[50vw] rounded border px-2 py-1.5 text-sm"
                        placeholder="Search title…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    {onCreate && (
                        <button
                            type="button"
                            className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                            onClick={onCreate}
                            disabled={!canEdit}
                            title={!canEdit ? '권한이 없습니다.' : undefined}
                        >
                            + New
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="p-4 text-sm text-gray-500">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No change orders.</div>
            ) : (
                <div className="overflow-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-xs text-gray-600">
                            <tr>
                                <th className="px-4 py-2 text-left">Title</th>
                                <th className="px-4 py-2 text-left">Status</th>
                                <th className="px-4 py-2 text-left">Due</th>
                                <th className="px-4 py-2 text-right">ΔCost</th>
                                <th className="px-4 py-2 text-right">ΔDays</th>
                                <th className="px-4 py-2 text-left">Updated</th>
                                <th className="px-4 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr
                                    key={r.id}
                                    className="border-t hover:bg-gray-50"
                                >
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={[
                                                    'inline-block w-2 h-2 rounded-full',
                                                    r.isOverdue ? 'bg-rose-500' : 'bg-transparent',
                                                ].join(' ')}
                                                title={r.isOverdue ? 'Overdue' : ''}
                                            />
                                            <div className="min-w-0">
                                                <div className="font-medium truncate">{r.title}</div>
                                                <div className="text-[11px] text-gray-500 truncate">
                                                    {r.nextAction ? `Next: ${r.nextAction}` : '—'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2">
                                        <span className={badge(r.status)}>{r.status}</span>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-700">{r.nextDueDate ?? '—'}</td>
                                    <td className="px-4 py-2 text-right tabular-nums text-xs">
                                        {r.costDelta == null ? '—' : String(r.costDelta)} {r.currency ?? ''}
                                    </td>
                                    <td className="px-4 py-2 text-right tabular-nums text-xs">
                                        {r.scheduleDeltaDays == null ? '—' : r.scheduleDeltaDays}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-600">
                                        {formatProjectDateOnly(r.updatedAt, zone, '—')}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        <button 
                                            onClick={() => onRowClick(r.id)}
                                            className="text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded transition-colors font-medium border border-slate-200"
                                            title={actionLabel === '계약으로 이동' ? '계약 상세 페이지의 CO 탭으로 이동합니다.' : '변경 내역 상세를 확인합니다.'}
                                        >
                                            {actionLabel}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
